// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@carbon/icons-react';
import {
  Button,
  Checkbox,
  CodeSnippet,
  InlineNotification,
  Loading,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToastNotification,
} from '@carbon/react';
import {
  accountTypeLabel,
  defaultGrantScope,
  iamRefusalMessage,
  isAwaitingFirstSignIn,
  isFederated,
  useDeleteUser,
  useResetPassword,
  useUserDetail,
} from '../../shared/users';
import type { UserRole } from '../../shared/users';
import { useSharedSession } from '../../shared/auth/useSharedSession';
import { roleLabel, roleTagType, ORG_ROLE_DESCRIPTION } from './orgRole';
import type { TagType } from './orgRole';
import styles from './UserDetailPage.module.css';

const AVATAR_CLASS_BY_TAG_TYPE: Record<TagType, string> = {
  purple: styles.roleOwner,
  green: styles.roleAdmin,
  blue: styles.roleMember,
  gray: styles.roleViewer,
};

function initialsOf(firstName: string, lastName: string, email: string): string {
  const first = firstName.trim().charAt(0);
  const last = lastName.trim().charAt(0);
  const initials = (first + last).toUpperCase();
  return initials || email.charAt(0).toUpperCase() || '?';
}

// A role-assignment grant date is account-audit metadata, not a site
// operational timestamp, so it renders in the viewer's own locale rather than
// through `useSiteTime()` (which assumes a single active site in context —
// not true on a cross-site screen like this one). `Intl.DateTimeFormat`
// directly, rather than `Date.prototype.toLocaleDateString`, so this doesn't
// trip the repo's site-timezone lint rule meant for operational timestamps.
const GRANTED_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return GRANTED_DATE_FORMAT.format(date);
}

// Same toast pattern, placement, and duration as BulkImportPage/UsersPage —
// one Admin Center convention for "action succeeded" feedback.
const TOAST_TIMEOUT_MS = 3000;

/**
 * Access > Users > user detail — route `users/:userId` (ADO #22697).
 * Organization Role and Site Access both edit real IAM role assignments
 * (`useUserDetail`, ADR-0026); the Deactivate/Reactivate button flips
 * `UserUpdate.status` between "active" and "inactive".
 *
 * Known gaps vs. the mockup, both out of this ticket's scope: there's no
 * backend source for a per-facility asset/machine count, so that stat is
 * omitted; and role edits always replace the single rank-selected role
 * rather than exposing every assignment a user might hold at once.
 */
export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const {
    user,
    isLoading,
    error,
    canEditAccess,
    effectiveRole,
    roleOptions,
    roleCatalogEmpty,
    setRole,
    removeAllRoles,
    isSettingRole,
    allSites,
    siteAccess,
    toggleSite,
    isTogglingSite,
    status,
    setStatus,
    isSettingStatus,
    mutationError,
  } = useUserDetail(userId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionToast, setActionToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // 'confirm' before IAM is called; the temporary password once it answers.
  // This state is its only copy (useResetPassword keeps none, and nothing goes
  // through the react-query cache); closing the dialog drops it.
  const [passwordDialog, setPasswordDialog] = useState<'confirm' | { password: string } | null>(
    null
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [noRoleOpen, setNoRoleOpen] = useState(false);
  // A role picked for a user who has none, waiting for the admin to say where
  // it applies: the whole organization or specific sites (AB#35975). Nothing is
  // sent until then.
  const [pendingGrant, setPendingGrant] = useState<{
    role: UserRole;
    scope: 'ORGANIZATION' | 'SITE';
  } | null>(null);
  const { permissions, email: sessionEmail } = useSharedSession();
  const { resetPassword, isResetting } = useResetPassword();
  const { deleteUser, isDeleting } = useDeleteUser();

  // Each action is gated on the caller's permission, never their role, and on
  // the account type: a federated user has no Helios password, and only a
  // pre-provisioned employee who hasn't signed in yet can be deleted
  // (AB#35253).
  const federated = user ? isFederated(user) : false;
  // IAM refuses a reset of your own account (400), so it isn't offered there.
  const isOwnProfile =
    !!user && !!sessionEmail && user.email.toLowerCase() === sessionEmail.toLowerCase();
  const canGeneratePassword =
    !!user && !federated && !isOwnProfile && permissions.includes('iam.user.password.reset');
  // IAM refuses to revoke your own admin role (AB#35966). The dropdown grants
  // the new role before revoking the old one, so it isn't offered at all.
  const isOwnAdminRole = isOwnProfile && effectiveRole === 'admin';
  // "No role" on your own profile would lock you out, whatever your role, and
  // IAM has no guard for that, so it isn't offered there. With no role at all
  // the option stays, as the select's current value.
  const canPickNoRole = !!effectiveRole && !isOwnProfile;
  const canDelete =
    !!user && isAwaitingFirstSignIn(user) && permissions.includes('iam.user.delete');

  // "pending" (AWAITING_FIRST_SIGN_IN) is a provisioning state, not a disabled
  // account — IAM already has `accountEnabled: true` for it. Treating it as
  // "Inactive" would offer a "Reactivate" button that's a no-op (the account
  // was never deactivated) and give no way to actually deactivate it.
  const isPending = status === 'pending';
  const isEnabled = status === 'active' || isPending;
  // Worded the same as the Users list, so one state reads one way everywhere.
  const statusLabel = isPending ? 'Awaiting first sign-in' : isEnabled ? 'Active' : 'Inactive';
  const name = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : '';

  // `setRole`/`toggleSite`/`setStatus` wrap `mutateAsync`, which — unlike
  // `mutate` — rejects on failure so a caller that wants to `await` it can.
  // These call sites are fire-and-forget: the failure is already surfaced via
  // `mutationError` (react-query's `isError`), so an uncaught `.catch(() =>
  // {})` here just prevents an unhandled-rejection crash, not a silent error.
  function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value as UserRole | '';
    if (!next) {
      // "No role" removes every assignment, so it is confirmed first.
      if (canPickNoRole) setNoRoleOpen(true);
      else if (!effectiveRole) setPendingGrant(null);
      return;
    }
    if (!effectiveRole) {
      const option = roleOptions.find(o => o.role === next);
      if (option && option.scopes.includes('SITE')) {
        setPendingGrant({ role: next, scope: defaultGrantScope(option) });
        return;
      }
      // Going straight to a role with no scope question: a pending pick is stale.
      setPendingGrant(null);
    }
    if (next !== effectiveRole) {
      setRole(next)
        .then(() => setActionToast(`${name} is now ${roleLabel(next)}`))
        .catch(() => {});
    }
  }

  function handleConfirmNoRole() {
    setNoRoleOpen(false);
    removeAllRoles()
      .then(() => setActionToast(`${name} is now ${roleLabel(null)}`))
      .catch(() => {});
  }

  function handleConfirmPendingOrg() {
    if (!pendingGrant) return;
    const { role } = pendingGrant;
    setPendingGrant(null);
    setRole(role)
      .then(() => setActionToast(`${name} is now ${roleLabel(role)}`))
      .catch(() => {});
  }

  function handleToggleSite(siteId: string, siteLabel: string, wasGranted: boolean) {
    if (pendingGrant && pendingGrant.scope === 'SITE' && !effectiveRole) {
      const { role } = pendingGrant;
      setPendingGrant(null);
      setRole(role, [siteId])
        .then(() => setActionToast(`${name} is now ${roleLabel(role)} at ${siteLabel}`))
        .catch(() => {});
      return;
    }
    toggleSite(siteId)
      .then(() =>
        setActionToast(`${name} ${wasGranted ? 'removed from' : 'added to'} ${siteLabel}`)
      )
      .catch(() => {});
  }

  function handleConfirmStatus() {
    setConfirmOpen(false);
    // Captured before the call: by the time it resolves, `status` (and so
    // `isEnabled`) has already flipped via the refetch, so the toast's verb
    // has to come from the action just taken, not the post-mutation state.
    const wasEnabled = isEnabled;
    setStatus(wasEnabled ? 'inactive' : 'active')
      .then(() => setActionToast(`${name} ${wasEnabled ? 'deactivated' : 'reactivated'}`))
      .catch(() => {});
  }

  function handleGeneratePassword() {
    if (!user) return;
    setActionError(null);
    resetPassword(user.id)
      .then(password => setPasswordDialog({ password }))
      .catch(() => {
        setPasswordDialog(null);
        setActionError(`Couldn't generate a temporary password for ${name}. Try again.`);
      });
  }

  function handleDeleteUser() {
    if (!user) return;
    setActionError(null);
    deleteUser(user.id)
      .then(() => navigate('/admin-center/users'))
      .catch(err => {
        setDeleteOpen(false);
        setActionError(iamRefusalMessage(err) ?? `Couldn't delete ${name}. Try again.`);
      });
  }

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => navigate('/admin-center/users')}
      >
        <ArrowLeft size={16} /> Back to Users
      </button>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title={error}
          hideCloseButton
          className={styles.notification}
        />
      )}
      {mutationError && (
        <InlineNotification
          kind="error"
          lowContrast
          title={mutationError}
          hideCloseButton
          className={styles.notification}
        />
      )}
      {actionError && (
        <InlineNotification
          kind="error"
          title={actionError}
          onCloseButtonClick={() => setActionError(null)}
          className={styles.notification}
        />
      )}

      {isLoading || !user ? (
        <div className={styles.loading}>
          <Loading withOverlay={false} small />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <span
              className={`${styles.avatar} ${AVATAR_CLASS_BY_TAG_TYPE[roleTagType(effectiveRole)]}`}
            >
              {initialsOf(user.first_name, user.last_name, user.email)}
            </span>
            <div className={styles.headerInfo}>
              <div className={styles.name}>{name}</div>
              <div className={styles.email}>{user.email}</div>
              <div className={styles.email}>{accountTypeLabel(user)}</div>
            </div>
            <span className={styles.statusCell}>
              <span className={isEnabled ? styles.statusDotActive : styles.statusDotInactive} />
              {statusLabel}
            </span>
            <Button
              kind={isEnabled ? 'danger--tertiary' : 'tertiary'}
              size="sm"
              disabled={isSettingStatus}
              onClick={() => setConfirmOpen(true)}
            >
              {isEnabled ? 'Deactivate account' : 'Reactivate account'}
            </Button>
            {canGeneratePassword && (
              <Button
                kind="tertiary"
                size="sm"
                disabled={isResetting}
                onClick={() => setPasswordDialog('confirm')}
              >
                Generate temporary password
              </Button>
            )}
            {canDelete && (
              <Button
                kind="danger--tertiary"
                size="sm"
                disabled={isDeleting}
                onClick={() => setDeleteOpen(true)}
              >
                Delete user
              </Button>
            )}
          </div>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Organization role</h2>
            <Select
              id="org-role-select"
              labelText="Organization role"
              hideLabel
              className={styles.roleSelect}
              value={effectiveRole ?? pendingGrant?.role ?? ''}
              disabled={
                isSettingRole || roleOptions.length === 0 || !canEditAccess || isOwnAdminRole
              }
              // helperText is wired to the select's aria-describedby, so a screen
              // reader hears why it's locked.
              helperText={
                isOwnAdminRole
                  ? "You can't change your own admin role. Ask another admin to do it."
                  : isOwnProfile && effectiveRole
                    ? "You can't remove all of your own roles. Ask another admin to do it."
                    : undefined
              }
              onChange={handleRoleChange}
            >
              {(canPickNoRole || !effectiveRole) && <SelectItem value="" text={roleLabel(null)} />}
              {effectiveRole && !roleOptions.some(o => o.role === effectiveRole) && (
                <SelectItem value={effectiveRole} text={roleLabel(effectiveRole)} />
              )}
              {roleOptions.map(option => (
                <SelectItem key={option.role} value={option.role} text={roleLabel(option.role)} />
              ))}
            </Select>
            {effectiveRole && (
              <p className={styles.roleDescription}>{ORG_ROLE_DESCRIPTION[effectiveRole]}</p>
            )}
            {!effectiveRole && pendingGrant && (
              <div className={styles.scopeChoice}>
                <RadioButtonGroup
                  legendText={`Where does ${roleLabel(pendingGrant.role)} apply?`}
                  name="pending-grant-scope"
                  valueSelected={pendingGrant.scope}
                  onChange={(value: string | number | undefined) =>
                    setPendingGrant({
                      role: pendingGrant.role,
                      scope: value === 'SITE' ? 'SITE' : 'ORGANIZATION',
                    })
                  }
                >
                  <RadioButton
                    labelText="The whole organization"
                    value="ORGANIZATION"
                    id="grant-scope-org"
                    disabled={
                      !roleOptions
                        .find(o => o.role === pendingGrant.role)
                        ?.scopes.includes('ORGANIZATION')
                    }
                  />
                  <RadioButton labelText="Specific sites" value="SITE" id="grant-scope-sites" />
                </RadioButtonGroup>
                {pendingGrant.scope === 'ORGANIZATION' ? (
                  <Button size="sm" disabled={isSettingRole} onClick={handleConfirmPendingOrg}>
                    {`Assign ${roleLabel(pendingGrant.role)}`}
                  </Button>
                ) : (
                  <p className={styles.sitesNote}>
                    Check the sites below where {roleLabel(pendingGrant.role)} applies. The first
                    one you check assigns the role.
                  </p>
                )}
              </div>
            )}
            {roleCatalogEmpty && (
              <InlineNotification
                kind="error"
                lowContrast
                hideCloseButton
                title="The role catalog is empty, so roles cannot be assigned."
                className={styles.notification}
              />
            )}
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Site access</h2>
            {!effectiveRole && !pendingGrant && (
              <p className={styles.sitesNote}>
                Pick an organization role first. Sites are granted for the role you choose.
              </p>
            )}
            {allSites && (
              <p className={styles.sitesNote}>
                This user holds organization-wide access — every site is granted and can&rsquo;t be
                revoked one at a time here.
              </p>
            )}
            <Table size="md">
              <TableHead>
                <TableRow>
                  <TableHeader>Access</TableHeader>
                  <TableHeader>Facility</TableHeader>
                  <TableHeader>Granted</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {siteAccess.map(site => (
                  <TableRow key={site.id}>
                    <TableCell>
                      <Checkbox
                        id={`site-access-${site.id}`}
                        labelText="Access"
                        hideLabel
                        checked={site.granted}
                        disabled={
                          site.locked ||
                          isTogglingSite ||
                          isSettingRole ||
                          !canEditAccess ||
                          (!effectiveRole && pendingGrant?.scope !== 'SITE')
                        }
                        onChange={() => handleToggleSite(site.id, site.label, site.granted)}
                      />
                    </TableCell>
                    <TableCell>
                      {site.label}
                      <span className={styles.region}>{site.region}</span>
                      {site.locked && !allSites && (
                        <span className={styles.region}> · Inherited from region</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(site.grantedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        </>
      )}

      <Modal
        open={confirmOpen}
        danger={isEnabled}
        modalHeading={isEnabled ? 'Deactivate this account?' : 'Reactivate this account?'}
        primaryButtonText={isEnabled ? 'Deactivate' : 'Reactivate'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setConfirmOpen(false)}
        onRequestSubmit={handleConfirmStatus}
        size="sm"
      >
        {/* "About 10 minutes" is IAM's CALLER_CACHE_TTL_SECONDS / AUTHZ_CACHE_TTL_SECONDS
            (600 s default); if those change, change this text too. */}
        <p>
          {/* IAM revokes roles on deactivation (AB#35979), but a user can still
              hold one: granted while inactive, or deactivated before that change.
              Only say they were removed when the lookup worked and found none; an
              unresolved lookup also sends roles: [], and unmapped roles still count. */}
          {isEnabled
            ? `${name} will lose access to Helios within about 10 minutes, and their roles will be removed.`
            : user?.access_resolved && user.roles.length === 0
              ? 'Reactivating restores sign-in only. Their roles were removed when they were deactivated, so grant roles again afterwards.'
              : `${name} will regain access to Helios with the roles they still hold.`}
        </p>
      </Modal>

      <Modal
        open={noRoleOpen}
        danger
        modalHeading="Remove all access?"
        primaryButtonText="Remove all roles"
        secondaryButtonText="Cancel"
        onRequestClose={() => setNoRoleOpen(false)}
        onRequestSubmit={handleConfirmNoRole}
        size="sm"
      >
        <p>
          This removes every role {name} holds, at the organization, each region and each site. They
          can still sign in but will see no sites or data until a role is granted again.
        </p>
      </Modal>

      <Modal
        open={passwordDialog === 'confirm'}
        danger
        modalHeading="Generate a temporary password?"
        primaryButtonText="Generate"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isResetting}
        onRequestClose={() => setPasswordDialog(null)}
        onRequestSubmit={handleGeneratePassword}
        size="sm"
      >
        <p>
          This replaces {name}&apos;s password and signs them out of every session. They&apos;ll be
          asked to choose a new password when they next sign in.
        </p>
      </Modal>

      <Modal
        open={passwordDialog !== null && passwordDialog !== 'confirm'}
        passiveModal
        modalHeading="Temporary password"
        onRequestClose={() => setPasswordDialog(null)}
        size="sm"
      >
        <p className={styles.passwordNote}>
          This is shown once. Copy it now and give it to {name} securely.
        </p>
        {passwordDialog !== null && passwordDialog !== 'confirm' && (
          <CodeSnippet type="single" feedback="Copied" aria-label="Temporary password">
            {passwordDialog.password}
          </CodeSnippet>
        )}
      </Modal>

      <Modal
        open={deleteOpen}
        danger
        modalHeading={`Delete ${name}?`}
        primaryButtonText="Delete account"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isDeleting}
        onRequestClose={() => setDeleteOpen(false)}
        onRequestSubmit={handleDeleteUser}
        size="sm"
      >
        <p>
          {name} hasn&apos;t signed in yet. Deleting revokes all their roles and removes their
          account. Inviting them again creates a new account.
        </p>
      </Modal>

      {actionToast && (
        <ToastNotification
          className={styles.toast}
          kind="success"
          title={actionToast}
          timeout={TOAST_TIMEOUT_MS}
          onClose={() => setActionToast(null)}
          lowContrast
        />
      )}
    </div>
  );
}
